/**
 * kg-search.js — Kalamazoo Gourmet search override
 *
 * Replaces Luxe's PredictiveSearch.getSearchResults with a call to our
 * Cloudflare Worker. Returns JSON; renders using Luxe's existing CSS classes.
 *
 * Load order: add this script tag AFTER predictive-search.js in theme.liquid,
 * also with defer. Both are deferred so DOM order guarantees correct execution order.
 *
 * <script src="{{ 'kg-search.js' | asset_url }}" defer="defer"></script>
 */

(function () {
  const WORKER_URL = "https://kg-search.mtiojanco.workers.dev";

  // ─── Analytics logging ───────────────────────────────────────────────────
  // Log only committed/final searches — never per-keystroke prefixes. A short
  // dedupe window (via sessionStorage) prevents the same query being counted
  // twice when, e.g., the user pauses typing and then presses Enter.
  const LOG_DEDUPE_MS = 10000;
  function logSearch(rawQuery) {
    const q = String(rawQuery || "").trim().toLowerCase();
    if (q.length < 2) return;
    try {
      const last = JSON.parse(sessionStorage.getItem("kg_last_log") || "null");
      if (last && last.q === q && Date.now() - last.t < LOG_DEDUPE_MS) return;
      sessionStorage.setItem("kg_last_log", JSON.stringify({ q: q, t: Date.now() }));
    } catch {}
    const url = `${WORKER_URL}/search-log`;
    const payload = JSON.stringify({ q: q });
    try {
      // sendBeacon survives page navigation (e.g. clicking a result)
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
        return;
      }
    } catch {}
    fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: payload, keepalive: true }).catch(() => {});
  }

  // ─── Full search results page pin injection ──────────────────────────────
  document.addEventListener("DOMContentLoaded", function () {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("q");
    if (!window.location.pathname.startsWith("/search") || !q) return;

    // Committed search — the user landed on the full results page
    logSearch(q);

    Promise.all([
      fetch(`${WORKER_URL}/search?q=${encodeURIComponent(q)}`, { headers: { Accept: "application/json" } }).then((r) => r.json()),
      fetch(`${WORKER_URL}/blocked`).then((r) => r.json()).catch(() => []),
    ]).then(([data, blockedUrls]) => {
        const blocked = new Set(blockedUrls || []);
        const results = data.results || [];
        const pins = results.filter((r) => r.type === "pinned");
        // Recipe-boosted articles: non-pinned articles from the Recipes blog.
        // The Worker returns them first in organic when recipe boost fires, so
        // we inject them here to match the overlay order. Deduped against pins.
        const pinnedUrls = new Set(pins.map((r) => r.url));
        const boostedArticles = results.filter(
          (r) => r.type === "article" &&
                 (r.blog_title || "").toLowerCase().includes("recipe") &&
                 !pinnedUrls.has(r.url)
        );

        // pins first, then boosted articles immediately after
        const toInject = [...pins, ...boostedArticles];

        const grid = document.querySelector("ul.grid.product-grid");
        if (!grid) return;

        // Remove blocked URLs from native grid
        if (blocked.size) {
          grid.querySelectorAll("li.grid__item a[href]").forEach((a) => {
            try {
              const path = new URL(a.href, location.origin).pathname;
              if (blocked.has(path)) {
                a.closest("li.grid__item")?.remove();
              }
            } catch {}
          });
        }

        // Convert any native article cards in the grid to product-card markup
        // so the entire results grid uses one consistent card style. Runs
        // regardless of whether we inject anything.
        normalizeArticleCards(grid);

        // Facet filtering/sorting re-renders the grid via AJAX, which brings
        // back native article-card markup. Watch the results container and
        // re-normalize whenever it changes so the grid stays consistent.
        observeGridForArticleCards();

        if (!toInject.length) return;

        // Remove native duplicates of items we're about to inject
        const injectUrls = new Set(toInject.map((r) => r.url));
        grid.querySelectorAll("li.grid__item a[href]").forEach((a) => {
          try {
            const path = new URL(a.href, location.origin).pathname;
            if (injectUrls.has(path)) {
              a.closest("li.grid__item")?.remove();
            }
          } catch {}
        });

        [...toInject].reverse().forEach((item) => {
          const li = document.createElement("li");
          li.className = "grid__item";
          li.innerHTML = productCardHtml(item);
          grid.prepend(li);
        });
      })
      .catch(() => {}); // silent fail — native results still show
    });

  // ─── Predictive search overlay override ──────────────────────────────────
  // Wait for the custom element to be defined, then patch its prototype.
  // This works because both scripts are defer — by DOMContentLoaded both have run.
  let idleLogTimer = null;
  let lastPredictiveTerm = "";
  document.addEventListener("DOMContentLoaded", function () {
    const proto = customElements.get("predictive-search")?.prototype;
    if (!proto) {
      console.warn("[kg-search] predictive-search element not found");
      return;
    }

    // Clicking a predictive result is a completed search — log the typed query.
    // (Delegated so it works across every re-render of the overlay.)
    document.addEventListener("click", function (e) {
      if (e.target.closest && e.target.closest("#predictive-search-results a[href]")) {
        clearTimeout(idleLogTimer);
        logSearch(lastPredictiveTerm);
      }
    });

    proto.getSearchResults = async function (searchTerm) {
      if (!searchTerm || searchTerm.trim().length < 3) { this.close(); return; }
      const self = this;

      // Log the FINAL typed query once the user pauses (~1.2s idle). The timer
      // resets on every keystroke, so partial prefixes are never logged.
      lastPredictiveTerm = searchTerm;
      clearTimeout(idleLogTimer);
      idleLogTimer = setTimeout(() => logSearch(searchTerm), 1200);

      try {
        const res = await fetch(
          `${WORKER_URL}/search?q=${encodeURIComponent(searchTerm)}`,
          { headers: { Accept: "application/json" } }
        );
        if (!res.ok) throw new Error(`Worker error ${res.status}`);
        const data = await res.json();
        self.predictiveSearchResults.innerHTML = renderResults(data, searchTerm);
        self.open();
      } catch (err) {
        console.error("[kg-search] search failed, falling back to Shopify", err);
        // Fall back to native Shopify section rendering
        fetch(
          `${window.routes?.predictive_search_url ?? "/search/suggest"}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`
        )
          .then((r) => r.text())
          .then((html) => {
            const parsed = new DOMParser().parseFromString(html, "text/html");
            const section = parsed.querySelector("#shopify-section-predictive-search");
            if (section) {
              self.predictiveSearchResults.innerHTML = section.innerHTML;
              self.open();
            }
          })
          .catch(() => self.close());
      }
    };
  });

  // ─── Renderer ──────────────────────────────────────────────────────────────

  function renderResults(data, query) {
    const results = data.results || [];
    if (!results.length) return renderEmpty(query);

    let html = '<div id="predictive-search-results" class="predictive-search-results">';

    // Flat, uncategorized list — results are shown in the order the Worker
    // returns them (pins first, then organic with recipe boost applied).
    // Each result still uses the card style appropriate to its type.
    html += '<ul role="listbox" class="predictive-search-list">';
    for (const r of results) {
      if (r.type === "pinned") {
        html += renderFeaturedCard(r);
      } else if (r.type === "product") {
        html += renderProductCard(r);
      } else {
        html += renderPageRow(r);
      }
    }
    html += "</ul>";

    html += `<div class="predictive-search-footer">
      <button class="button" onclick="window.location='/search?q=${encodeURIComponent(query)}'">
        Search for &ldquo;${escHtml(query)}&rdquo;
      </button>
    </div>`;

    html += "</div>";
    return html;
  }

  function renderFeaturedCard(r) {
    const imgHtml = r.image
      ? `<img src="${escAttr(r.image)}" alt="${escAttr(r.title)}" width="600" height="600" loading="lazy">`
      : "";
    return `
      <li role="option" class="predictive-search-product-card">
        <a href="${escAttr(r.url)}">
          <div class="predictive-search-card-image ps-card-ratio-square">
            <div class="card-product--badges pc-badges-top-left"></div>
            ${imgHtml}
          </div>
          <span>${escHtml(r.title)}</span>
        </a>
      </li>`;
  }

  function renderProductCard(r) {
    const imgHtml = r.image
      ? `<img src="${escAttr(r.image)}" alt="${escAttr(r.title)}" width="600" height="600" loading="lazy">`
      : "";
    const priceHtml = r.price
      ? `<span><div class="price"><div class="price__container">
           <div class="price__regular">
             <span class="visually-hidden visually-hidden--inline">Regular price</span>
             <span class="price-item price-item--regular">${escHtml(r.price)} USD</span>
           </div>
         </div></div></span>`
      : "";
    return `
      <li role="option" class="predictive-search-product-card">
        <a href="${escAttr(r.url)}">
          <div class="predictive-search-card-image ps-card-ratio-square">
            <div class="card-product--badges pc-badges-top-left"></div>
            ${imgHtml}
          </div>
          <span>${escHtml(r.title)}</span>
          ${priceHtml}
        </a>
      </li>`;
  }

  function renderPageRow(r) {
    if (r.image) {
      return `
        <li role="option" class="predictive-search-product-card">
          <a href="${escAttr(r.url)}">
            <div class="predictive-search-card-image ps-card-ratio-square">
              <img src="${escAttr(r.image)}" alt="${escAttr(r.title)}" width="600" height="600" loading="lazy">
            </div>
            <span>${escHtml(r.title)}</span>
          </a>
        </li>`;
    }
    return `
      <li role="option" class="predictive-search-page-list">
        <a href="${escAttr(r.url)}">
          <span>${escHtml(r.title)}</span>
        </a>
      </li>`;
  }

  function renderEmpty(query) {
    return `<div id="predictive-search-results" class="predictive-search-results">
      <div class="predictive-search-footer">
        <button class="button" onclick="window.location='/search?q=${encodeURIComponent(query)}'">
          Search for &ldquo;${escHtml(query)}&rdquo;
        </button>
      </div>
    </div>`;
  }

  // ─── Full-page card builders ────────────────────────────────────────────────
  // Injected results use the native Luxe product-card markup. Native article
  // cards (which Shopify renders with a different component) are rewritten to
  // the same product-card markup so the whole grid is visually consistent.

  let gridObserver = null;
  function observeGridForArticleCards() {
    if (gridObserver || !window.MutationObserver) return;
    // Observe a stable ancestor — facets replace the <ul> itself, so watching
    // the container catches full grid swaps as well as child changes.
    const container = document.getElementById("product-grid") ||
      document.querySelector("ul.grid.product-grid")?.parentElement;
    if (!container) return;

    let scheduled = false;
    gridObserver = new MutationObserver(() => {
      if (scheduled) return;
      const grid = document.querySelector("ul.grid.product-grid");
      // Nothing to do if there are no native article cards left
      if (!grid || !grid.querySelector(".article-card")) return;
      scheduled = true;
      // Defer so we run once after the AJAX re-render settles. Our own DOM
      // writes trigger the observer again, but the next pass finds no
      // .article-card and exits, so this can't loop.
      requestAnimationFrame(() => {
        scheduled = false;
        const g = document.querySelector("ul.grid.product-grid");
        if (g) normalizeArticleCards(g);
      });
    });
    gridObserver.observe(container, { childList: true, subtree: true });
  }

  function normalizeArticleCards(grid) {
    grid.querySelectorAll("li.grid__item .article-card").forEach((card) => {
      try {
        const link = card.querySelector("a[href]");
        const img = card.querySelector("img");
        const heading = card.querySelector(".article-card-heading, h3");
        if (!link || !heading) return;
        const item = {
          // Strip Shopify's tracking query params to keep the URL clean
          url: link.getAttribute("href").split("?")[0],
          title: heading.textContent.trim(),
          image: img ? img.getAttribute("src") : "",
        };
        const li = card.closest("li.grid__item");
        if (li) li.innerHTML = productCardHtml(item);
      } catch {}
    });
  }

  function productCardHtml(item) {
    const imgHtml = item.image
      ? `<div class="card-media"><a href="${escAttr(item.url)}" title="${escAttr(item.title)}"><div class="card-product--badges pc-badges-top-left"></div><div class="card-product-image-outer"><div class="card-product--image"><div class="card-product--image-wrapper card-product--single-image-wrapper"><img src="${escAttr(item.image)}" alt="${escAttr(item.title)}" loading="lazy" width="800" height="800"></div></div></div></a></div>`
      : "";
    return `<div class="card card-product card-ratio-square">
      ${imgHtml}
      <a href="${escAttr(item.url)}" class="card-product--text-section-link">
        <div class="card-product--text-section">
          <div class="card-product--title">
            <h3 class="card-product-title-h3 card-title-font-family-body">${escHtml(item.title)}</h3>
            <span class="card-product-type-label"></span>
          </div>
        </div>
      </a>
    </div>`;
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function escAttr(str) {
    return String(str).replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }
})();
